/**
 * calcServiceEstimate.ts — live on-screen total for the Service estimates form.
 * Proposed home: src/utils/calcServiceEstimate.ts (Alpha Insights front end).
 *
 * Mirrors service_estimate_calc.py. The server recalculates and is the source of
 * truth; this exists so the tech sees the total as they type. Must return the same
 * values as the Python test cases (see calcServiceEstimate.test.ts).
 */

export interface MarkupBand { minCost: number; markupRate: number; }
export interface ServiceRates {
  markupBands: MarkupBand[];
  laborRates: Record<string, number>;
  internalLaborCost: number;
}
export interface MaterialLineInput { description: string; alphaCost: number; }
export interface LaborLineInput { description: string; hours: number; laborType: string; }
export interface EstimateInput {
  materials: MaterialLineInput[];
  labor: LaborLineInput[];
  taxRate: number;          // e.g. 0.0825; from the job site's tax jurisdiction
  freightOn: boolean;
  freightRate: number;      // e.g. 0.016377
  discount: number;
}
export interface MaterialLineResult extends MaterialLineInput { markupRate: number; markedUp: number; afterTax: number; }
export interface LaborLineResult extends LaborLineInput { rate: number; lineTotal: number; }
export interface EstimateResult {
  materialLines: MaterialLineResult[];
  laborLines: LaborLineResult[];
  alphaCostTotal: number;
  materialTotal: number;
  laborTotal: number;
  laborHours: number;
  freight: number;
  discount: number;
  subtotal: number;
  totalPrice: number;
  internalLaborCost: number;
  internalMaterialCost: number;
  margin: number | null;
  errors: string[];
}

export const DEFAULT_SERVICE_RATES: ServiceRates = {
  markupBands: [
    { minCost: 0, markupRate: 1.0 }, { minCost: 101, markupRate: 0.8 }, { minCost: 301, markupRate: 0.6 },
    { minCost: 1001, markupRate: 0.4 }, { minCost: 2001, markupRate: 0.25 },
  ],
  laborRates: {
    'ME-Contract': 175, 'ME-Preferred': 185, 'ME-Spot': 195,
    'CE-Contract': 185, 'CE-Preferred': 195, 'CE-Spot': 205,
  },
  internalLaborCost: 132,
};

const cents = (v: number): number => Math.round((v + Number.EPSILON) * 100) / 100;

export const markupFor = (cost: number, bands: MarkupBand[] = DEFAULT_SERVICE_RATES.markupBands): number => {
  if (!(cost > 0)) return 0;
  const sorted = [...bands].sort((a, b) => b.minCost - a.minCost);
  const band = sorted.find((b) => cost >= b.minCost);
  return band ? band.markupRate : sorted[sorted.length - 1].markupRate;
};

/** Excel CEILING.MATH(x, 5), after rounding to cents so 2915.00 stays 2915. */
export const roundUpTo5 = (v: number): number => {
  const c = cents(v);
  return c > 0 ? Math.ceil(c / 5) * 5 : 0;
};

export const calcServiceEstimate = (input: EstimateInput, rates: ServiceRates = DEFAULT_SERVICE_RATES): EstimateResult => {
  const errors: string[] = [];
  const tax = input.taxRate || 0;

  const mats = input.materials.map((m) => {
    const cost = Number(m.alphaCost) || 0;
    if (cost < 0) errors.push(`Negative material cost on "${m.description}"`);
    const markupRate = markupFor(cost, rates.markupBands);
    const marked = cost * (1 + markupRate);
    return { ...m, alphaCost: cost, markupRate, markedUp: marked, afterTax: marked * (1 + tax) };
  });
  const labs = input.labor.map((l) => {
    const hours = Number(l.hours) || 0;
    if (hours < 0) errors.push(`Negative hours on "${l.description}"`);
    const rate = rates.laborRates[l.laborType];
    if (rate === undefined) errors.push(`Unknown labor type "${l.laborType}"`);
    return { ...l, hours, rate: rate ?? 0, lineTotal: hours * (rate ?? 0) };
  });

  const alphaCostTotal = mats.reduce((a, m) => a + m.alphaCost, 0);
  const materialTotal = mats.reduce((a, m) => a + m.afterTax, 0);
  const laborTotal = labs.reduce((a, l) => a + l.lineTotal, 0);
  const laborHours = labs.reduce((a, l) => a + l.hours, 0);
  const freight = input.freightOn ? materialTotal * (input.freightRate || 0) : 0;
  const discount = Number(input.discount) || 0;
  const beforeDiscount = materialTotal + laborTotal + freight;
  if (discount < 0 || discount > beforeDiscount) errors.push('Discount must be between $0 and the subtotal');
  const subtotal = beforeDiscount - discount;
  const totalPrice = roundUpTo5(subtotal);

  const internalLaborCost = laborHours * rates.internalLaborCost;
  const internalMaterialCost = (alphaCostTotal + freight) * (1 + tax);
  const margin = totalPrice > 0 ? Math.round(((totalPrice - (internalLaborCost + internalMaterialCost)) / totalPrice) * 10000) / 10000 : null;

  return {
    materialLines: mats.map((m) => ({ ...m, markedUp: cents(m.markedUp), afterTax: cents(m.afterTax) })),
    laborLines: labs.map((l) => ({ ...l, lineTotal: cents(l.lineTotal) })),
    alphaCostTotal: cents(alphaCostTotal),
    materialTotal: cents(materialTotal),
    laborTotal: cents(laborTotal),
    laborHours,
    freight: cents(freight),
    discount: cents(discount),
    subtotal: cents(subtotal),
    totalPrice,
    internalLaborCost: cents(internalLaborCost),
    internalMaterialCost: cents(internalMaterialCost),
    margin,
    errors,
  };
};

/** Rates from GET /service-estimates/settings/ (decimals arrive as strings). */
export const ratesFromSettings = (b: {
  markup_bands: { min_cost: string; markup_rate: string }[];
  labor_types: { code: string; rate: string }[];
  settings: { internal_labor_cost: string };
}): ServiceRates => ({
  markupBands: b.markup_bands.map((m) => ({ minCost: Number(m.min_cost), markupRate: Number(m.markup_rate) })),
  laborRates: Object.fromEntries(b.labor_types.map((t) => [t.code, Number(t.rate)])),
  internalLaborCost: Number(b.settings.internal_labor_cost),
});
